import { computed, effect, inject, Injector, signal, Service, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { getAppOrigin } from '@despesse/core';
import type { AuthError, Session, User } from '@supabase/supabase-js';
import { SupabaseService } from './supabase.service';

/**
 * Session-aware auth API for the console (email/password + password recovery via Supabase).
 * Auth failures throw `Error` whose `message` is an `auth/{code}` key for ngx-translate.
 */
@Service()
export class AuthService {
  private readonly supabase = inject(SupabaseService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(Injector);

  private readonly sessionSignal = signal<Session | null>(null);
  private readonly readySignal = signal(false);
  private readonly recoverySignal = signal(false);

  /** Current Supabase session. */
  readonly session = this.sessionSignal.asReadonly();
  /** Current authenticated user, if any. */
  readonly user = computed<User | null>(() => this.sessionSignal()?.user ?? null);
  /** True when a non-anonymous session is active. */
  readonly isAuthenticated = computed(() => !!this.sessionSignal()?.user);
  /**
   * True when the JWT has `app_metadata.console_admin` (platform console access).
   * Commerce customers must not pass this check.
   */
  readonly isConsoleAdmin = computed(
    () => this.sessionSignal()?.user?.app_metadata?.['console_admin'] === true,
  );
  /** True after the initial session has been resolved. */
  readonly ready = this.readySignal.asReadonly();
  /** True when the user landed from a password-recovery link. */
  readonly isPasswordRecovery = this.recoverySignal.asReadonly();

  constructor() {
    this.init().catch(console.error);
  }

  private async init(): Promise<void> {
    const { data } = await this.supabase.auth.getSession();
    this.sessionSignal.set(data.session);

    this.supabase.auth.onAuthStateChange((event, session) => {
      this.sessionSignal.set(session);

      if (event === 'PASSWORD_RECOVERY') {
        this.recoverySignal.set(true);
        this.router.navigate(['/auth/update-password']).catch(console.error);
      }

      if (event === 'SIGNED_OUT') {
        this.recoverySignal.set(false);
      }
    });

    this.readySignal.set(true);
  }

  /**
   * Promise bridge for guards: waits until the initial auth session is resolved.
   * Prefer reading `ready` in templates.
   * @returns Resolves when `ready` becomes true.
   */
  async waitUntilReady(): Promise<void> {
    if (this.ready()) {
      return;
    }
    await new Promise<void>((resolve) => {
      const watcher = effect(
        () => {
          if (this.ready()) {
            untracked(() => watcher.destroy());
            resolve();
          }
        },
        { injector: this.injector },
      );
    });
  }

  /**
   * Signs in with email and password.
   * Rejects users without `app_metadata.console_admin` (signs them out).
   *
   * @param email - Account email.
   * @param password - Account password.
   * @returns Resolves when a console-admin session is established.
   */
  async signIn(email: string, password: string): Promise<void> {
    const { data, error } = await this.supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error) {
      this.throwAuthError(error);
    }
    if (data.user?.app_metadata?.['console_admin'] !== true) {
      await this.supabase.auth.signOut();
      throw new Error('auth/forbidden');
    }
    await this.syncLanguagePreference();
  }

  /**
   * Signs out without navigating (for guards that redirect themselves).
   *
   * @returns Resolves when the session is cleared.
   */
  async clearSession(): Promise<void> {
    await this.supabase.auth.signOut();
    this.recoverySignal.set(false);
  }

  /**
   * Sends a password-reset email (magic link / OTP according to Supabase templates).
   * Redirects back to the update-password page after confirmation.
   * Email language comes from the user's `user_metadata.language` (default FR in the template).
   */
  async resetPassword(email: string): Promise<void> {
    const { error } = await this.supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${getAppOrigin()}/auth/update-password`,
    });
    if (error) {
      this.throwAuthError(error);
    }
  }

  /** Sets a new password for the current (recovery) session. */
  async updatePassword(password: string): Promise<void> {
    const { error } = await this.supabase.auth.updateUser({
      password,
      data: { language: this.currentLanguage() },
    });
    if (error) {
      this.throwAuthError(error);
    }
    this.recoverySignal.set(false);
  }

  /** Signs out and navigates to the login page. */
  async signOut(): Promise<void> {
    const { error } = await this.supabase.auth.signOut();
    if (error) {
      this.throwAuthError(error);
    }
    await this.router.navigate(['/auth/login']);
  }

  /**
   * Persists the UI language in `user_metadata.language` so auth emails
   * (Go templates via `.Data.language`) can pick FR/EN.
   */
  private async syncLanguagePreference(): Promise<void> {
    const language = this.currentLanguage();
    if (this.user()?.user_metadata?.['language'] === language) {
      return;
    }
    await this.supabase.auth.updateUser({ data: { language } });
  }

  private currentLanguage(): string {
    return this.translate.getCurrentLang() || this.translate.getFallbackLang() || 'fr';
  }

  /** Throws `auth/{supabaseCode}` for ngx-translate (no message mapping here). */
  private throwAuthError(error: AuthError): never {
    throw new Error(error.code ? `auth/${error.code}` : 'auth/unknown');
  }
}
